import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, Trophy, Clock, TrendingUp, Play, Crown,
  Focus, SplitSquareVertical, Brain, MessageSquare, Hand,
  Database, Eye, Zap, Lightbulb, AlertCircle, CreditCard, XCircle, RefreshCw,
  Mail, Bell, Settings, BarChart3, Users, Share2, Twitter, Linkedin, Copy, Facebook,
  Gift, X, Sparkles, Timer, BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { PageLayout } from '../components/Layout';
import { SEO, PageSEO } from '../components/SEO';
import { apiClient, useAuth } from '../lib/api';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  BarChart, Bar, Cell
} from 'recharts';

const iconMap = {
  Focus: Focus,
  SplitSquareVertical: SplitSquareVertical,
  Brain: Brain,
  MessageSquare: MessageSquare,
  Hand: Hand,
  Database: Database,
  Eye: Eye,
  Zap: Zap,
  Lightbulb: Lightbulb,
  AlertCircle: AlertCircle,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [tests, setTests] = useState([]);
  const [progress, setProgress] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [percentiles, setPercentiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUpgradeOffer, setShowUpgradeOffer] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [emailSubscribed, setEmailSubscribed] = useState(user?.email_subscribed ?? true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showSpecialOffer, setShowSpecialOffer] = useState(false);
  const [dismissingOffer, setDismissingOffer] = useState(false);
  const [guideOfferStatus, setGuideOfferStatus] = useState(null);
  const [guideOfferLoading, setGuideOfferLoading] = useState(false);
  const [dismissingGuideOffer, setDismissingGuideOffer] = useState(false);

  const formatAccessDate = (isoDate) => {
    if (!isoDate) return null;
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getRemainingAccessLabel = (isoDate) => {
    if (!isoDate) return null;
    const endDate = new Date(isoDate);
    if (Number.isNaN(endDate.getTime())) return null;

    const now = Date.now();
    const diffMs = endDate.getTime() - now;
    if (diffMs <= 0) return 'Access ends today';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} remaining`;
    }
    return `${Math.max(hours, 1)} hour${hours === 1 ? '' : 's'} remaining`;
  };

  // Check if user should see the special offer
  const shouldShowSpecialOffer = () => {
    if (!user) return false;
    if (user.is_member) return false; // Already a member
    if (user.dismissed_special_offer) return false; // Already dismissed
    
    // Check if credits are exhausted
    const creditsExhausted = (user.free_credits || 0) <= 0;
    
    // Check if account is 5+ days old
    const accountAge = user.created_at ? 
      (Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24) : 0;
    const accountOldEnough = accountAge >= 5;
    
    return creditsExhausted || accountOldEnough;
  };

  useEffect(() => {
    setShowSpecialOffer(shouldShowSpecialOffer());
  }, [user]);

  useEffect(() => {
    Promise.all([
      apiClient.getTests(),
      apiClient.getProgress(),
      apiClient.getScoreHistory(),
      apiClient.getPercentiles(),
    ])
      .then(([testsRes, progressRes, historyRes, percentilesRes]) => {
        setTests(testsRes.data);
        setProgress(progressRes.data);
        setScoreHistory(historyRes.data);
        setPercentiles(percentilesRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    
    // Fetch subscription status if user is a member
    if (user?.is_member) {
      apiClient.getSubscriptionStatus()
        .then(res => setSubscriptionStatus(res.data))
        .catch(() => setSubscriptionStatus(null));

      apiClient.getGuideOfferStatus()
        .then(res => setGuideOfferStatus(res.data))
        .catch(() => setGuideOfferStatus(null));
    }
  }, [user, location.key]);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const res = await apiClient.cancelSubscription();
      toast.success(res.data.message);
      setSubscriptionStatus(prev => ({
        ...prev,
        subscription_status: 'cancelling',
        cancel_at_period_end: true,
        membership_expires_at: res.data.access_until
      }));
      setShowCancelDialog(false);
      setShowUpgradeOffer(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
    setCancelLoading(false);
  };

  const handleUpgradeToAnnual = async () => {
    setUpgradeLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.createCheckoutSession({
        plan_type: 'retention_upgrade', // Special retention offer - one-time payment
        success_url: `${originUrl}/dashboard?upgraded=true`,
        cancel_url: `${originUrl}/dashboard`
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setUpgradeLoading(false);
    }
  };

  // Check if user is on PREMIUM (monthly £7.99) plan - ONLY these users see the upgrade offer
  // Must be: subscription_plan === 'premium' AND status === 'active'
  // NOT for: annual (complete), single_test, legacy members, or any other plan
  const isPremiumMonthlySubscriber = 
    subscriptionStatus?.subscription_status === 'active' && 
    subscriptionStatus?.subscription_plan === 'premium' &&
    !subscriptionStatus?.is_legacy;

  const handleReactivateSubscription = async () => {
    setCancelLoading(true);
    try {
      const res = await apiClient.reactivateSubscription();
      toast.success(res.data.message);
      setSubscriptionStatus(prev => ({
        ...prev,
        subscription_status: 'active',
        cancel_at_period_end: false,
        membership_expires_at: null
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
    setCancelLoading(false);
  };

  const handleEmailPreferenceChange = async (checked) => {
    setEmailLoading(true);
    try {
      await apiClient.setEmailPreference(checked);
      setEmailSubscribed(checked);
      toast.success(checked ? 'Subscribed to marketing emails' : 'Unsubscribed from marketing emails');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to update email preferences');
      setEmailSubscribed(!checked);
    }
    setEmailLoading(false);
  };

  const handleDismissSpecialOffer = async () => {
    setDismissingOffer(true);
    try {
      await apiClient.dismissSpecialOffer();
      setShowSpecialOffer(false);
      toast.success('Offer dismissed');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to dismiss offer');
    }
    setDismissingOffer(false);
  };

  const handleDismissGuideOffer = async () => {
    setDismissingGuideOffer(true);
    try {
      await apiClient.dismissGuideOffer();
      setGuideOfferStatus(prev => (prev ? { ...prev, visible: false, dismissed: true } : prev));
      toast.success('Guide offer hidden');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to dismiss guide offer');
    }
    setDismissingGuideOffer(false);
  };

  const handleClaimGuideOffer = async () => {
    setGuideOfferLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.createGuideCheckout(originUrl, true);
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      toast.error('Unable to start guide checkout');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'You already own this guide') {
        toast.success('You already own the guide.');
      } else {
        toast.error(detail || 'Failed to start guide checkout');
      }
      await apiClient.getGuideOfferStatus()
        .then(res => setGuideOfferStatus(res.data))
        .catch(() => {});
    }
    setGuideOfferLoading(false);
  };

  const getSpecialOfferLink = () => {
    const baseUrl = 'https://buy.stripe.com/cNi5kE3q53Ib3Da2ml2go0i';
    // Append client_reference_id so webhook knows who paid
    return `${baseUrl}?client_reference_id=${user?.id || ''}`;
  };

  // Calculate stats
  const totalAttempts = progress.reduce((sum, p) => sum + p.total_attempts, 0);
  const avgAccuracy = progress.length > 0 
    ? progress.reduce((sum, p) => sum + p.average_accuracy, 0) / progress.length 
    : 0;
  const testsStarted = progress.length;
  const testsCompleted = progress.filter(p => p.highest_level >= 5).length;

  // Prepare chart data
  const chartData = scoreHistory.slice(0, 20).reverse().map((score, i) => ({
    name: `#${i + 1}`,
    score: score.score,
    accuracy: score.accuracy,
  }));

  // Prepare radar chart data for skills assessment
  const radarData = tests.map(test => {
    const testProgress = progress.find(p => p.test_slug === test.slug);
    return {
      skill: test.name.replace(' (Selective)', '').replace(' (Divided)', '').split(' ')[0],
      fullName: test.name,
      accuracy: testProgress?.average_accuracy || 0,
      completion: testProgress ? (testProgress.highest_level / test.total_levels) * 100 : 0,
      maxScore: 100,
    };
  });

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO {...PageSEO.dashboard} />
      {/* Header */}
      <section className="py-8 bg-[#0B1D51]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Welcome back, {user?.name?.split(' ')[0]}
                </h1>
                {user?.is_member && (
                  <Badge className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium
                  </Badge>
                )}
              </div>
              <p className="text-white/70">Track your progress and continue your practice</p>
            </div>
            {!user?.is_member && (
              <Link to="/pricing">
                <Button className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90" data-testid="upgrade-btn">
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Special Offer Banner - for free users who meet criteria */}
      <AnimatePresence>
        {showSpecialOffer && (
          <motion.section 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative bg-gradient-to-r from-[#FF9F1C] via-[#FF7F00] to-[#FF9F1C] py-4 sm:py-6"
            data-testid="special-offer-banner"
          >
            {/* Animated background sparkles */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-2 left-[10%] w-2 h-2 bg-white/30 rounded-full animate-pulse" />
              <div className="absolute top-4 right-[20%] w-1.5 h-1.5 bg-white/20 rounded-full animate-pulse delay-300" />
              <div className="absolute bottom-3 left-[30%] w-1 h-1 bg-white/25 rounded-full animate-pulse delay-500" />
            </div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                {/* Offer Content */}
                <div className="flex items-center gap-4 text-center lg:text-left">
                  <div className="hidden sm:flex items-center justify-center w-16 h-16 bg-white/20 rounded-full">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center justify-center lg:justify-start gap-2 mb-1">
                      <Sparkles className="w-5 h-5 text-white animate-pulse" />
                      <span className="text-white font-bold text-lg sm:text-xl">Limited Time Offer</span>
                    </div>
                    <h2 className="text-white text-xl sm:text-2xl font-bold mb-1">
                      Get Full Year Access for Just £14.99
                    </h2>
                    <p className="text-white/90 text-sm sm:text-base">
                      One-time payment • No subscription • Unlimited practice for 12 months
                    </p>
                  </div>
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <a 
                    href={getSpecialOfferLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#0B1D51] font-bold rounded-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    data-testid="special-offer-cta"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    Claim Offer Now
                  </a>
                  
                  <button
                    onClick={handleDismissSpecialOffer}
                    disabled={dismissingOffer}
                    className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                    data-testid="dismiss-offer-btn"
                  >
                    <X className="w-4 h-4" />
                    {dismissingOffer ? 'Dismissing...' : 'No thanks'}
                  </button>
                </div>
              </div>
              
              {/* One-time offer warning */}
              <div className="mt-3 flex items-center justify-center lg:justify-start gap-2 text-white/70 text-xs">
                <Timer className="w-3.5 h-3.5" />
                <span>This offer will not be shown again if dismissed</span>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {guideOfferStatus?.visible && (
          <motion.section
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            className="py-3 bg-[#F3F8FF] border-b border-[#D8E5FB]"
            data-testid="guide-offer-pill"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="rounded-2xl border border-[#BFD3F7] bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#E8EEF9] flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#0B1D51]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0B1D51]">Subscriber Guide Offer</p>
                    <p className="text-sm text-[#334155]">
                      Unlock the detailed Psychometric Guide for <strong>£{guideOfferStatus.discount_price?.toFixed?.(2) || '4.45'}</strong>
                      {' '}instead of £{guideOfferStatus.full_price?.toFixed?.(2) || '19.99'}.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleClaimGuideOffer}
                    disabled={guideOfferLoading}
                    className="bg-[#0B1D51] hover:bg-[#0B1D51]/90"
                    data-testid="guide-offer-claim-btn"
                  >
                    {guideOfferLoading ? 'Redirecting...' : 'Claim Guide Offer'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDismissGuideOffer}
                    disabled={dismissingGuideOffer}
                    className="text-[#64748B]"
                    data-testid="guide-offer-dismiss-btn"
                  >
                    {dismissingGuideOffer ? 'Hiding...' : 'Dismiss'}
                  </Button>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Subscription Management Section - Visible for members and active subscribers */}
      {subscriptionStatus && (subscriptionStatus.has_subscription || user?.is_member) && (
        <section className="py-6 bg-white border-b" data-testid="subscription-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-slate-200" data-testid="subscription-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-[#0B1D51]" />
                    <CardTitle className="text-lg">Subscription</CardTitle>
                  </div>
                  {subscriptionStatus.is_legacy ? (
                    <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid="subscription-badge-legacy">
                      Legacy Plan
                    </Badge>
                  ) : subscriptionStatus.subscription_status === 'cancelling' ? (
                    <Badge variant="outline" className="text-red-600 border-red-300" data-testid="subscription-badge-cancelling">
                      Cancelled
                    </Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700" data-testid="subscription-badge-active">
                      Active
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="space-y-1">
                    {subscriptionStatus.is_legacy ? (
                      <>
                        <p className="text-sm text-[#64748B]">
                          You have a legacy membership that does not auto-renew.
                        </p>
                        {subscriptionStatus.membership_expires_at && (
                          <p className="text-sm font-medium">
                            Access until: {new Date(subscriptionStatus.membership_expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        )}
                      </>
                    ) : subscriptionStatus.subscription_status === 'cancelling' ? (
                      <>
                        <p className="text-sm text-[#64748B]">
                          Your subscription has been cancelled. You will keep access until your current paid period ends.
                        </p>
                        {formatAccessDate(subscriptionStatus.membership_expires_at) && (
                          <p className="text-sm font-medium">
                            Access until: {formatAccessDate(subscriptionStatus.membership_expires_at)}
                          </p>
                        )}
                        {getRemainingAccessLabel(subscriptionStatus.membership_expires_at) && (
                          <p className="text-sm font-medium text-[#0B1D51]">
                            Time remaining: {getRemainingAccessLabel(subscriptionStatus.membership_expires_at)}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-[#64748B]">
                          Plan: <span className="font-medium text-[#0B1D51] capitalize">{subscriptionStatus.subscription_plan?.replace('_', ' ') || 'Premium'}</span>
                        </p>
                        {subscriptionStatus.access_type === 'single' && subscriptionStatus.single_test_access && (
                          <p className="text-sm text-[#64748B]">
                            Test Access: <span className="font-medium">{subscriptionStatus.single_test_access}</span>
                          </p>
                        )}
                        <p className="text-sm text-[#64748B]">Your subscription renews automatically.</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!subscriptionStatus.is_legacy && subscriptionStatus.subscription_status === 'cancelling' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReactivateSubscription}
                        disabled={cancelLoading}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        data-testid="reactivate-subscription-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {cancelLoading ? 'Processing...' : 'Reactivate'}
                      </Button>
                    ) : !subscriptionStatus.is_legacy && subscriptionStatus.has_subscription ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="cancel-subscription-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={(open) => {
        setShowCancelDialog(open);
        if (!open) setShowUpgradeOffer(false);
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            {!showUpgradeOffer ? (
              <>
                <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                <AlertDialogDescription>
                  {subscriptionStatus?.membership_expires_at ? (
                    <>
                      Your subscription will remain active until <strong>{formatAccessDate(subscriptionStatus.membership_expires_at)}</strong>.
                      {' '}After cancellation, you will not be charged again.
                    </>
                  ) : (
                    <>
                      Your subscription will remain active until the end of your current billing period.
                      {' '}After cancellation, you will not be charged again.
                    </>
                  )}
                </AlertDialogDescription>
              </>
            ) : (
              <>
                <AlertDialogTitle className="text-[#0B1D51]">
                  🎯 Before You Go - Special Offer!
                </AlertDialogTitle>
                <div className="space-y-4 mt-2 text-sm text-[#64748B]">
                  <p>
                    Instead of cancelling, upgrade to <span className="font-semibold text-[#0B1D51]">a full year of access</span> for 
                    just <span className="font-bold text-green-600">£14.99</span> (one-time payment, no recurring charges)?
                  </p>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <p className="font-semibold text-[#0B1D51]">Special Retention Offer - Save £80+!</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span><strong>One-time payment</strong> - No more monthly charges</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span><strong>Psychometric Guide</strong> included with your subscription</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span><strong>RSSB Review (T1379)</strong> - Tests are being updated following the live RSSB review of train driver psychometrics. Your continued subscription ensures access to all updated tests.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>Full year of unlimited access to all 14 practice tests</span>
                      </li>
                    </ul>
                  </div>
                  
                  <p className="text-xs text-[#94A3B8] italic">
                    Your current monthly subscription will be cancelled and replaced with the annual plan.
                  </p>
                </div>
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            {!showUpgradeOffer ? (
              <>
                <AlertDialogCancel data-testid="keep-subscription-btn">Keep Subscription</AlertDialogCancel>
                {isPremiumMonthlySubscriber && (
                  <Button
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    onClick={() => setShowUpgradeOffer(true)}
                    data-testid="see-upgrade-options-btn"
                  >
                    See Upgrade Options
                  </Button>
                )}
                <AlertDialogAction
                  onClick={handleCancelSubscription}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={cancelLoading}
                  data-testid="confirm-cancel-btn"
                >
                  {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                </AlertDialogAction>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setShowUpgradeOffer(false)}
                  className="text-[#64748B]"
                  data-testid="back-from-upgrade-btn"
                >
                  ← Back
                </Button>
                <AlertDialogAction
                  onClick={handleCancelSubscription}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={cancelLoading}
                  data-testid="cancel-anyway-btn"
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel Anyway'}
                </AlertDialogAction>
                <Button
                  onClick={handleUpgradeToAnnual}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={upgradeLoading}
                  data-testid="upgrade-now-btn"
                >
                  {upgradeLoading ? 'Redirecting...' : 'Upgrade Now - £14.99'}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section className="py-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats Grid */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            {[
              { label: 'Total Attempts', value: totalAttempts, icon: Target, color: '#0B1D51' },
              { label: 'Avg. Accuracy', value: `${avgAccuracy.toFixed(1)}%`, icon: TrendingUp, color: '#2CB67D' },
              { label: 'Tests Started', value: testsStarted, icon: Play, color: '#FF9F1C' },
              { label: 'Tests Completed', value: testsCompleted, icon: Trophy, color: '#7C3AED' },
            ].map((stat, i) => (
              <motion.div key={i} variants={itemVariants}>
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${stat.color}10` }}
                      >
                        <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B] uppercase tracking-wide">{stat.label}</p>
                        <p className="font-mono text-xl font-bold text-[#0B1D51]">{stat.value}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <Tabs defaultValue="progress" className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
              <TabsTrigger value="percentile" data-testid="tab-percentile">
                <BarChart3 className="w-4 h-4 mr-1" />
                Percentile
              </TabsTrigger>
              <TabsTrigger value="skills" data-testid="tab-skills">Skills Assessment</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">Score History</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="progress">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tests.map(test => {
                  const testProgress = progress.find(p => p.test_slug === test.slug);
                  const IconComponent = iconMap[test.icon] || Target;
                  const completion = testProgress 
                    ? (testProgress.highest_level / test.total_levels) * 100 
                    : 0;

                  return (
                    <motion.div 
                      key={test.id} 
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <Card className="border-slate-200 card-hover h-full">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="w-10 h-10 bg-[#0B1D51]/5 rounded-sm flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-[#0B1D51]" />
                            </div>
                            {testProgress && (
                              <span className="font-mono text-sm text-[#2CB67D] font-medium">
                                {testProgress.average_accuracy.toFixed(0)}% acc
                              </span>
                            )}
                          </div>
                          <CardTitle className="font-heading text-lg text-[#0B1D51] mt-3">
                            {test.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#64748B]">Progress</span>
                                <span className="font-mono">
                                  {testProgress?.highest_level || 0}/{test.total_levels}
                                </span>
                              </div>
                              <Progress value={completion} className="h-2" />
                            </div>
                            {testProgress && (
                              <div className="flex justify-between text-sm">
                                <span className="text-[#64748B]">Best Score</span>
                                <span className="font-mono font-medium text-[#0B1D51]">
                                  {testProgress.best_score}
                                </span>
                              </div>
                            )}
                            <Link to={`/tests/${test.slug}`}>
                              <Button 
                                variant="outline" 
                                className="w-full mt-2"
                                data-testid={`continue-${test.slug}`}
                              >
                                {testProgress ? 'Continue' : 'Start'}
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Percentile Tab */}
            <TabsContent value="percentile">
              <div className="space-y-6">
                {/* Overall Percentile Card */}
                <Card className="border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#0B1D51] to-[#1a3a8f] p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-[#FF9F1C]" />
                      </div>
                      <div>
                        <h3 className="text-white font-heading text-xl font-bold">Overall Percentile</h3>
                        <p className="text-white/70 text-sm">Compared to all users</p>
                      </div>
                    </div>
                    
                    {percentiles?.overall_percentile != null ? (
                      <div className="flex items-end justify-between">
                        <div className="flex items-end gap-4">
                          <div className="text-6xl font-mono font-bold text-white">
                            {percentiles.overall_percentile}
                            <span className="text-2xl text-white/70">th</span>
                          </div>
                          <div className="pb-2">
                            <p className="text-white/80 text-sm">
                              You're performing better than <span className="font-bold text-[#FF9F1C]">{percentiles.overall_percentile}%</span> of all users
                            </p>
                            <p className="text-white/60 text-xs mt-1">
                              Based on {percentiles.total_users_compared} users compared
                            </p>
                          </div>
                        </div>
                        
                        {/* Share Button */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              className="bg-white/20 hover:bg-white/30 text-white border-0"
                              data-testid="share-percentile-btn"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem 
                              onClick={() => {
                                const text = `I'm in the ${percentiles.overall_percentile}th percentile on TDF Psychometrics! 🚂 Train driver aptitude tests. Can you beat my score?`;
                                const url = 'https://traindriverpsychometrics.co.uk';
                                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                              }}
                              className="cursor-pointer"
                            >
                              <Twitter className="w-4 h-4 mr-2 text-[#1DA1F2]" />
                              Share on X (Twitter)
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                const url = 'https://traindriverpsychometrics.co.uk';
                                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`I'm in the ${percentiles.overall_percentile}th percentile on TDF Psychometrics!`)}`, '_blank');
                              }}
                              className="cursor-pointer"
                            >
                              <Facebook className="w-4 h-4 mr-2 text-[#4267B2]" />
                              Share on Facebook
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                const text = `I'm in the ${percentiles.overall_percentile}th percentile on TDF Psychometrics - Train Driver aptitude test preparation!`;
                                const url = 'https://traindriverpsychometrics.co.uk';
                                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                              }}
                              className="cursor-pointer"
                            >
                              <Linkedin className="w-4 h-4 mr-2 text-[#0077B5]" />
                              Share on LinkedIn
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                const text = `🚂 I'm in the ${percentiles.overall_percentile}th percentile on TDF Psychometrics!\n\nPractice train driver aptitude tests: https://traindriverpsychometrics.co.uk\n\nCan you beat my score?`;
                                navigator.clipboard.writeText(text);
                                toast.success('Copied to clipboard!');
                              }}
                              className="cursor-pointer"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy to Clipboard
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : (
                      <div className="text-white/70 text-center py-4">
                        <p>Complete some tests to see your percentile ranking</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Percentile Progress Bar */}
                  {percentiles?.overall_percentile !== null && (
                    <div className="p-4 bg-white">
                      <div className="relative h-6 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div 
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#0B1D51] to-[#FF9F1C] rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${percentiles.overall_percentile}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                        <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-mono">
                          <span className="text-slate-400">0%</span>
                          <span className={percentiles.overall_percentile > 50 ? "text-white font-bold" : "text-slate-600 font-bold"}>
                            {percentiles.overall_percentile}th percentile
                          </span>
                          <span className="text-slate-400">100%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>

                {/* Test-by-Test Percentiles */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Percentile by Test
                    </CardTitle>
                    <CardDescription>See how you rank in each assessment category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {percentiles?.test_percentiles?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Bar Chart */}
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              data={percentiles.test_percentiles.map(tp => {
                                const test = tests.find(t => t.slug === tp.test_slug);
                                return {
                                  name: test?.name?.split(' ')[0] || tp.test_slug,
                                  fullName: test?.name || tp.test_slug,
                                  percentile: tp.percentile,
                                  score: tp.user_score,
                                  users: tp.total_users
                                };
                              })}
                              layout="vertical"
                              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                              <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 12 }} />
                              <Tooltip 
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                                        <p className="font-semibold text-[#0B1D51]">{data.fullName}</p>
                                        <p className="text-sm text-[#64748B]">Percentile: <span className="font-bold text-[#FF9F1C]">{data.percentile}th</span></p>
                                        <p className="text-sm text-[#64748B]">Your Score: <span className="font-mono">{data.score}</span></p>
                                        <p className="text-xs text-slate-400">vs {data.users} users</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Bar dataKey="percentile" radius={[0, 4, 4, 0]}>
                                {percentiles.test_percentiles.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={entry.percentile >= 75 ? '#2CB67D' : entry.percentile >= 50 ? '#FF9F1C' : entry.percentile >= 25 ? '#0B1D51' : '#94A3B8'}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Legend */}
                        <div className="flex flex-wrap justify-center gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm bg-[#2CB67D]" />
                            <span className="text-[#64748B]">Top 25% (75-100)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm bg-[#FF9F1C]" />
                            <span className="text-[#64748B]">Above Average (50-74)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm bg-[#0B1D51]" />
                            <span className="text-[#64748B]">Below Average (25-49)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm bg-[#94A3B8]" />
                            <span className="text-[#64748B]">Bottom 25% (0-24)</span>
                          </div>
                        </div>

                        {/* Detailed List */}
                        <div className="mt-6 space-y-3">
                          {percentiles.test_percentiles.map((tp, i) => {
                            const test = tests.find(t => t.slug === tp.test_slug);
                            const IconComponent = test ? (iconMap[test.icon] || Target) : Target;
                            const color = tp.percentile >= 75 ? '#2CB67D' : tp.percentile >= 50 ? '#FF9F1C' : tp.percentile >= 25 ? '#0B1D51' : '#94A3B8';
                            
                            return (
                              <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                                  <IconComponent className="w-5 h-5" style={{ color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-[#0B1D51] truncate">{test?.name || tp.test_slug}</p>
                                  <p className="text-xs text-[#64748B]">Score: {tp.user_score} • vs {tp.total_users} users</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-mono font-bold" style={{ color }}>
                                    {tp.percentile}<span className="text-sm">th</span>
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-[#64748B]">Complete some tests to see your percentile rankings</p>
                        <Link to="/tests">
                          <Button className="mt-4 bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                            Start a Test
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="skills">
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51]">
                      Skills Assessment
                    </CardTitle>
                    <CardDescription>Your performance across all assessment areas</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[400px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                          <PolarGrid stroke="#E2E8F0" />
                          <PolarAngleAxis 
                            dataKey="skill" 
                            tick={{ fill: '#64748B', fontSize: 11 }}
                            tickLine={false}
                          />
                          <PolarRadiusAxis 
                            angle={30} 
                            domain={[0, 100]} 
                            tick={{ fill: '#64748B', fontSize: 10 }}
                            tickCount={5}
                          />
                          <Radar
                            name="Accuracy %"
                            dataKey="accuracy"
                            stroke="#0B1D51"
                            fill="#0B1D51"
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                          <Radar
                            name="Completion %"
                            dataKey="completion"
                            stroke="#FF9F1C"
                            fill="#FF9F1C"
                            fillOpacity={0.2}
                            strokeWidth={2}
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white',
                              border: '1px solid #E2E8F0',
                              borderRadius: '4px',
                            }}
                            formatter={(value, name) => [`${value.toFixed(1)}%`, name]}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51]">
                      Strengths & Development Areas
                    </CardTitle>
                    <CardDescription>Focus on areas that need improvement</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Strengths */}
                      <div>
                        <h4 className="text-sm font-semibold text-[#2CB67D] mb-2 flex items-center gap-2">
                          <Trophy className="w-4 h-4" />
                          Strengths
                        </h4>
                        <div className="space-y-2">
                          {radarData
                            .filter(d => d.accuracy >= 70)
                            .sort((a, b) => b.accuracy - a.accuracy)
                            .slice(0, 3)
                            .map((item, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-[#2CB67D]/5 rounded">
                                <span className="text-sm text-[#0B1D51]">{item.fullName}</span>
                                <Badge className="bg-[#2CB67D]/10 text-[#2CB67D]">
                                  {item.accuracy.toFixed(0)}%
                                </Badge>
                              </div>
                            ))}
                          {radarData.filter(d => d.accuracy >= 70).length === 0 && (
                            <p className="text-sm text-[#64748B] italic">
                              Keep practicing to identify your strengths!
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Development Areas */}
                      <div>
                        <h4 className="text-sm font-semibold text-[#FF9F1C] mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Areas for Development
                        </h4>
                        <div className="space-y-2">
                          {radarData
                            .filter(d => d.accuracy > 0 && d.accuracy < 70)
                            .sort((a, b) => a.accuracy - b.accuracy)
                            .slice(0, 3)
                            .map((item, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-[#FF9F1C]/5 rounded">
                                <span className="text-sm text-[#0B1D51]">{item.fullName}</span>
                                <Badge className="bg-[#FF9F1C]/10 text-[#FF9F1C]">
                                  {item.accuracy.toFixed(0)}%
                                </Badge>
                              </div>
                            ))}
                          {radarData.filter(d => d.accuracy > 0 && d.accuracy < 70).length === 0 && (
                            <p className="text-sm text-[#64748B] italic">
                              {radarData.some(d => d.accuracy >= 70) 
                                ? 'Great job! All your scores are strong.'
                                : 'Start practicing tests to see your development areas.'}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Not Started */}
                      {radarData.filter(d => d.accuracy === 0).length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#64748B] mb-2 flex items-center gap-2">
                            <Play className="w-4 h-4" />
                            Not Yet Started
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {radarData
                              .filter(d => d.accuracy === 0)
                              .map((item, i) => (
                                <Badge key={i} variant="outline" className="text-[#64748B]">
                                  {item.skill}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="history">
              <div className="grid lg:grid-cols-3 gap-6">
                <Card className="border-slate-200 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51]">
                      Performance Trend
                    </CardTitle>
                    <CardDescription>Your recent scores and accuracy</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {chartData.length > 0 ? (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'white',
                                border: '1px solid #E2E8F0',
                                borderRadius: '4px',
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="score" 
                              stroke="#0B1D51" 
                              strokeWidth={2}
                              dot={{ fill: '#0B1D51' }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="accuracy" 
                              stroke="#2CB67D" 
                              strokeWidth={2}
                              dot={{ fill: '#2CB67D' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-[#64748B]">
                        Complete some tests to see your performance trend
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51]">
                      Recent Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {scoreHistory.length > 0 ? (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {scoreHistory.slice(0, 10).map((score, i) => {
                          const test = tests.find(t => t.slug === score.test_slug);
                          return (
                            <div 
                              key={i}
                              className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                            >
                              <div>
                                <p className="text-sm font-medium text-[#0F172A]">
                                  {test?.name || score.test_slug}
                                </p>
                                <p className="text-xs text-[#64748B]">
                                  Level {score.level}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-mono text-sm font-medium text-[#0B1D51]">
                                  {score.score}
                                </p>
                                <p className="text-xs text-[#2CB67D]">
                                  {score.accuracy.toFixed(0)}%
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[#64748B] text-center py-8">
                        No activity yet. Start practicing!
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="grid gap-6">
                {/* Email Preferences */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading text-[#0B1D51] flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#FF9F1C]" />
                      Email Preferences
                    </CardTitle>
                    <CardDescription>
                      Manage what emails you receive from TDF Psychometrics
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FF9F1C]/10 rounded-full flex items-center justify-center">
                          <Bell className="w-5 h-5 text-[#FF9F1C]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0B1D51]">Marketing Emails</p>
                          <p className="text-sm text-[#64748B]">
                            Receive tips, offers, and updates about new features
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={emailSubscribed}
                        onCheckedChange={handleEmailPreferenceChange}
                        disabled={emailLoading}
                        data-testid="email-subscribe-toggle"
                      />
                    </div>
                    <p className="text-xs text-[#64748B]">
                      Note: You will always receive important account-related emails such as password resets and subscription confirmations.
                    </p>
                  </CardContent>
                </Card>

                {/* Account Information */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading text-[#0B1D51] flex items-center gap-2">
                      <Settings className="w-5 h-5 text-[#FF9F1C]" />
                      Account Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-[#64748B]">Email</span>
                      <span className="font-medium text-[#0B1D51]">{user?.email}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-[#64748B]">Name</span>
                      <span className="font-medium text-[#0B1D51]">{user?.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-[#64748B]">Free Credits</span>
                      <span className="font-medium text-[#2CB67D]">{user?.free_credits || 0} remaining</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-[#64748B]">Member Since</span>
                      <span className="font-medium text-[#0B1D51]">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        }) : 'N/A'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </PageLayout>
  );
};

export default DashboardPage;
